import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CenterSpinner, EmptyState } from "@/components/common/States";
import { cn } from "@/lib/utils";

const STATUSES = ["all", "pending_approval", "approved", "suspended", "rejected"];
const TONE = {
  approved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  pending_approval: "bg-[hsl(var(--warning))]/20 text-[hsl(27_60%_32%)]",
  suspended: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
};

export default function AdminVendors() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-vendors", filter],
    queryFn: () => api(`/admin/vendors${filter !== "all" ? `?status=${filter}` : ""}`),
  });

  const setStatus = async (v, status) => {
    try {
      await api(`/admin/vendors/${v.vendor_id}`, { method: "PATCH", json: { status } });
      toast.success(`${v.name} → ${status.replace("_", " ")}`);
      qc.invalidateQueries({ queryKey: ["admin-vendors"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <StaffShell items={adminNav} roleLabel="Admin" title="Vendors">
      <div className="mb-4 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`vendor-filter-${s}`}
            className={cn("rounded-full border px-3.5 py-1.5 text-sm capitalize transition-colors", filter === s ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted")}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {isLoading ? (
        <CenterSpinner />
      ) : !data?.length ? (
        <EmptyState title="No vendors" />
      ) : (
        <Card className="overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Store</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((v) => (
                <TableRow key={v.vendor_id} data-testid={`admin-vendor-${v.vendor_id}`}>
                  <TableCell>
                    <p className="font-medium">{v.name}</p>
                    <p className="text-xs text-muted-foreground">{v.email}</p>
                  </TableCell>
                  <TableCell>{v.category}</TableCell>
                  <TableCell>
                    <Badge className={cn("rounded-full font-normal", TONE[v.status])}>{v.status.replace("_", " ")}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {v.status !== "approved" && (
                        <Button size="sm" className="rounded-full" onClick={() => setStatus(v, "approved")} data-testid={`approve-${v.vendor_id}`}>Approve</Button>
                      )}
                      {v.status === "approved" && (
                        <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => setStatus(v, "suspended")} data-testid={`suspend-${v.vendor_id}`}>Suspend</Button>
                      )}
                      {v.status === "pending_approval" && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(v, "rejected")} data-testid={`reject-${v.vendor_id}`}>Reject</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </StaffShell>
  );
}
